import React, { useState, useEffect, useCallback } from 'react';
import { Lock, Plus, Pencil, Trash2, Upload, X, FileText, Save, LogOut } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEO from '../components/SEO';
import { getPapers, adminLogin, adminCheckToken, adminCreatePaper, adminUpdatePaper, adminDeletePaper } from '../services/api';

const STATUS_OPTIONS = ['Published', 'Awaiting Peer Review', 'Independent'];

const statusColors = {
  'Published': 'bg-emerald-500/15 text-emerald-400',
  'Awaiting Peer Review': 'bg-amber-500/15 text-amber-400',
  'Independent': 'bg-[#8FB8D4]/15 text-[#8FB8D4]',
};

const AdminPage = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [token, setToken] = useState(null);
  const [password, setPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(true);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingPaper, setEditingPaper] = useState(null);

  // Form state
  const [form, setForm] = useState({
    title: '', abstract: '', status: 'Independent', tags: '', publishDate: '', author: 'Calum Knox',
  });
  const [pdfFile, setPdfFile] = useState(null);
  const [saving, setSaving] = useState(false);

  // Check for existing token on mount
  useEffect(() => {
    const existingToken = sessionStorage.getItem('admin_token');
    if (existingToken) {
      adminCheckToken(existingToken)
        .then(() => {
          setToken(existingToken);
          setAuthenticated(true);
        })
        .catch(() => {
          sessionStorage.removeItem('admin_token');
        })
        .finally(() => setAuthLoading(false));
    } else {
      setAuthLoading(false);
    }
  }, []);

  const fetchPapers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPapers();
      setPapers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authenticated) fetchPapers();
  }, [authenticated, fetchPapers]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthError('');
    try {
      const res = await adminLogin(password);
      const newToken = res.data.token;
      sessionStorage.setItem('admin_token', newToken);
      setToken(newToken);
      setAuthenticated(true);
      setPassword('');
    } catch (err) {
      setAuthError(err.response?.status === 401 ? 'Invalid password' : 'Login failed');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_token');
    setToken(null);
    setAuthenticated(false);
    setPassword('');
  };

  const resetForm = () => {
    setForm({ title: '', abstract: '', status: 'Independent', tags: '', publishDate: '', author: 'Calum Knox' });
    setPdfFile(null);
    setEditingPaper(null);
    setShowForm(false);
  };

  const openCreateForm = () => {
    resetForm();
    setForm(f => ({ ...f, publishDate: new Date().toISOString().split('T')[0] }));
    setShowForm(true);
  };

  const openEditForm = (paper) => {
    setEditingPaper(paper);
    setForm({
      title: paper.title,
      abstract: paper.abstract,
      status: paper.status,
      tags: paper.tags.join(', '),
      publishDate: paper.publishDate,
      author: paper.author,
    });
    setPdfFile(null);
    setShowForm(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('title', form.title);
      fd.append('abstract', form.abstract);
      fd.append('status', form.status);
      fd.append('tags', form.tags);
      fd.append('publishDate', form.publishDate);
      fd.append('author', form.author);
      if (pdfFile) fd.append('pdf', pdfFile);

      if (editingPaper) {
        await adminUpdatePaper(editingPaper.id, fd, token);
      } else {
        await adminCreatePaper(fd, token);
      }
      resetForm();
      fetchPapers();
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        setAuthError('Session expired. Please log in again.');
      } else {
        console.error('Save error:', err);
        alert('Error saving publication');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (paper) => {
    if (!window.confirm(`Delete "${paper.title}"?`)) return;
    try {
      await adminDeletePaper(paper.id, token);
      fetchPapers();
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
        setAuthError('Session expired. Please log in again.');
      } else {
        console.error('Delete error:', err);
      }
    }
  };

  // Loading check for existing session
  if (authLoading) {
    return (
      <Layout>
        <SEO title="Admin" noIndex />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-pulse text-[#5A7A85]">Verifying session...</div>
        </div>
      </Layout>
    );
  }

  // Login screen
  if (!authenticated) {
    return (
      <Layout>
        <SEO title="Admin" noIndex />
        <div className="min-h-screen flex items-center justify-center px-6">
          <div className="w-full max-w-sm">
            <div className="card-premium p-8 text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#1D546D]/20 flex items-center justify-center mx-auto mb-6">
                <Lock size={28} className="text-[#8FB8D4]" />
              </div>
              <h1 className="text-2xl font-bold text-[#F3F4F4] mb-2">Admin Access</h1>
              <p className="text-[#5A7A85] text-sm mb-8">Enter password to manage publications</p>

              <form onSubmit={handleLogin}>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Password"
                  className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.15)] rounded-lg py-3 px-4 text-[#F3F4F4] placeholder-[#5A7A85] text-sm focus:outline-none focus:border-[#1D546D] transition-colors mb-4"
                  data-testid="admin-password-input"
                  autoFocus
                />
                {authError && <p className="text-[#9B2335] text-sm mb-4" data-testid="auth-error">{authError}</p>}
                <button
                  type="submit"
                  className="btn-primary w-full justify-center"
                  data-testid="admin-login-btn"
                >
                  Sign In
                </button>
              </form>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <SEO title="Admin - Publications" noIndex />

      <div className="pt-24 lg:pt-32 pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-10">
            <div>
              <h1 className="text-3xl font-bold text-[#F3F4F4]" data-testid="admin-title">Manage Publications</h1>
              <p className="text-[#5A7A85] text-sm mt-1">{papers.length} publication{papers.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={openCreateForm}
                className="btn-primary inline-flex"
                data-testid="add-paper-btn"
              >
                <Plus size={18} className="mr-2" /> New Publication
              </button>
              <button
                onClick={handleLogout}
                className="btn-secondary inline-flex"
                data-testid="logout-btn"
              >
                <LogOut size={16} className="mr-2" /> Sign Out
              </button>
            </div>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
              <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-[#0d3042] border border-[rgba(139,164,172,0.15)] rounded-2xl shadow-2xl">
                <div className="flex items-center justify-between p-6 border-b border-[rgba(139,164,172,0.1)]">
                  <h2 className="text-xl font-bold text-[#F3F4F4]">
                    {editingPaper ? 'Edit Publication' : 'New Publication'}
                  </h2>
                  <button onClick={resetForm} className="text-[#5A7A85] hover:text-[#F3F4F4] transition-colors">
                    <X size={20} />
                  </button>
                </div>

                <form onSubmit={handleSave} className="p-6 space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-[#8BA4AC] mb-1.5">Title *</label>
                    <input
                      type="text"
                      required
                      value={form.title}
                      onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))}
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.15)] rounded-lg py-2.5 px-4 text-[#F3F4F4] text-sm focus:outline-none focus:border-[#1D546D] transition-colors"
                      data-testid="form-title"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#8BA4AC] mb-1.5">Abstract *</label>
                    <textarea
                      required
                      rows={4}
                      value={form.abstract}
                      onChange={(e) => setForm(f => ({ ...f, abstract: e.target.value }))}
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.15)] rounded-lg py-2.5 px-4 text-[#F3F4F4] text-sm focus:outline-none focus:border-[#1D546D] transition-colors resize-none"
                      data-testid="form-abstract"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-[#8BA4AC] mb-1.5">Status *</label>
                      <select
                        value={form.status}
                        onChange={(e) => setForm(f => ({ ...f, status: e.target.value }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.15)] rounded-lg py-2.5 px-4 text-[#F3F4F4] text-sm focus:outline-none focus:border-[#1D546D] transition-colors"
                        data-testid="form-status"
                      >
                        {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-[#8BA4AC] mb-1.5">Publish Date *</label>
                      <input
                        type="date"
                        required
                        value={form.publishDate}
                        onChange={(e) => setForm(f => ({ ...f, publishDate: e.target.value }))}
                        className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.15)] rounded-lg py-2.5 px-4 text-[#F3F4F4] text-sm focus:outline-none focus:border-[#1D546D] transition-colors"
                        data-testid="form-date"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#8BA4AC] mb-1.5">Author</label>
                    <input
                      type="text"
                      value={form.author}
                      onChange={(e) => setForm(f => ({ ...f, author: e.target.value }))}
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.15)] rounded-lg py-2.5 px-4 text-[#F3F4F4] text-sm focus:outline-none focus:border-[#1D546D] transition-colors"
                      data-testid="form-author"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#8BA4AC] mb-1.5">Tags (comma-separated)</label>
                    <input
                      type="text"
                      value={form.tags}
                      onChange={(e) => setForm(f => ({ ...f, tags: e.target.value }))}
                      placeholder="AI, Defence, Autonomous Systems"
                      className="w-full bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.15)] rounded-lg py-2.5 px-4 text-[#F3F4F4] placeholder-[#5A7A85] text-sm focus:outline-none focus:border-[#1D546D] transition-colors"
                      data-testid="form-tags"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#8BA4AC] mb-1.5">
                      PDF {editingPaper?.pdfFilename ? '(replace existing)' : ''}
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        accept=".pdf"
                        onChange={(e) => setPdfFile(e.target.files[0] || null)}
                        className="hidden"
                        id="pdf-upload"
                        data-testid="form-pdf"
                      />
                      <label
                        htmlFor="pdf-upload"
                        className="flex items-center justify-center gap-3 w-full py-8 border-2 border-dashed border-[rgba(139,164,172,0.15)] rounded-xl cursor-pointer hover:border-[#1D546D] hover:bg-[rgba(255,255,255,0.02)] transition-all"
                      >
                        <Upload size={20} className="text-[#5A7A85]" />
                        <span className="text-sm text-[#8BA4AC]">
                          {pdfFile ? pdfFile.name : 'Click to upload PDF'}
                        </span>
                      </label>
                    </div>
                    {editingPaper?.pdfFilename && !pdfFile && (
                      <p className="text-xs text-[#5A7A85] mt-2 flex items-center gap-1">
                        <FileText size={12} /> Current: {editingPaper.pdfFilename}
                      </p>
                    )}
                  </div>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={saving}
                      className="btn-primary flex-1 justify-center"
                      data-testid="form-save-btn"
                    >
                      <Save size={16} className="mr-2" />
                      {saving ? 'Saving...' : editingPaper ? 'Update' : 'Create'}
                    </button>
                    <button type="button" onClick={resetForm} className="btn-secondary">
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Papers Table */}
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-pulse text-[#5A7A85]">Loading...</div>
            </div>
          ) : papers.length > 0 ? (
            <div className="space-y-3" data-testid="admin-papers-list">
              {papers.map(paper => (
                <div key={paper.id} className="card-premium flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`admin-paper-${paper.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusColors[paper.status] || ''}`}>
                        {paper.status}
                      </span>
                      {paper.pdfFilename && (
                        <span className="text-[#8FB8D4] text-xs flex items-center gap-1"><FileText size={11} /> PDF</span>
                      )}
                      <span className="text-[#5A7A85] text-xs">{paper.publishDate}</span>
                    </div>
                    <h3 className="text-[#F3F4F4] font-semibold truncate">{paper.title}</h3>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {paper.tags.map(t => (
                        <span key={t} className="text-[#5A7A85] text-xs">#{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => openEditForm(paper)}
                      className="p-2.5 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.1)] text-[#8BA4AC] hover:text-[#F3F4F4] hover:border-[#1D546D] transition-all"
                      data-testid={`edit-${paper.id}`}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(paper)}
                      className="p-2.5 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(139,164,172,0.1)] text-[#8BA4AC] hover:text-[#9B2335] hover:border-[#9B2335]/30 transition-all"
                      data-testid={`delete-${paper.id}`}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24 card-premium">
              <FileText size={48} className="text-[#5A7A85]/30 mx-auto mb-6" />
              <p className="text-xl text-[#8BA4AC] mb-4">No publications yet</p>
              <button onClick={openCreateForm} className="btn-primary inline-flex" data-testid="first-paper-btn">
                <Plus size={18} className="mr-2" /> Add Your First Publication
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default AdminPage;
